import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, type KnowledgeTestResult } from '../lib/api.ts'
import { brandToMergeFields, useBrandKit } from '../brand/BrandKitContext.tsx'
import { dropLeadingBlockquote, wrapTables } from '../lib/doc-structure.ts'
import { renderMarkdown } from '../lib/markdown.ts'
import { RenderedDoc } from './RenderedDoc.tsx'

/**
 * The topic's knowledge test: authored questions rendered without a model
 * (see server/knowledge-test.ts), plus the answer key. Both are deterministic,
 * so the answer key rides in RenderedDoc's `extra` slot under a plain label —
 * unlike the meeting guide's AI questions, it needs no "AI-assisted" warning.
 */
export function KnowledgeTestPage() {
  const { topicId = '' } = useParams()
  const { brand } = useBrandKit()
  const [result, setResult] = useState<KnowledgeTestResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setResult(null)
    setError(null)
    api.knowledgeTest(topicId, brandToMergeFields(brand))
      .then(setResult)
      .catch(() => setError('Could not load this knowledge test. Please try again.'))
  }, [topicId, brand])

  if (error) return <main className="tool"><p className="notice notice--error">{error}</p></main>
  if (!result) return <main className="tool"><p className="notice">Loading…</p></main>

  const isDraft = result.reviewStatus !== 'approved'
  const rendered = renderMarkdown(result.answerKeyMarkdown)
  const answerKeyHtml = wrapTables(isDraft ? dropLeadingBlockquote(rendered) : rendered)

  return (
    <RenderedDoc
      title={result.title}
      markdown={result.markdown}
      disclaimer={result.disclaimer}
      reviewStatus={result.reviewStatus}
      // `title` is the topic's own name (server/knowledge-test.ts merges it
      // straight from the topics row), so the trail can be named without a
      // second request. The department level is the one thing this route does
      // not know, so the trail steps from the hub straight to the topic.
      crumbs={[
        { label: 'Departments', to: '/' },
        { label: result.title, to: `/topics/${topicId}` },
        { label: 'Knowledge test' },
      ]}
      extra={
        <section>
          <p className="eyebrow">Answer key</p>
          {/* The server stamps the draft notice onto the answer key as well as
              the question sheet, so that either can be copied out on its own
              and still carry the warning. On screen they sit one above the
              other under a single banner, so this copy is the third. */}
          <div className="document" dangerouslySetInnerHTML={{ __html: answerKeyHtml }} />
        </section>
      }
    />
  )
}
